import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import "./App.css";

// Build marker — bump this string whenever you need to force a fresh
// frontend bundle hash (busts CDN / build caches that don't pick up
// changes to deeply-nested component files like ExamImporter).
// Last bump: 2026-05-25 — Arabic filename fix for docx import.
const __BUILD_MARKER__ = "2026-05-25-arabic-filename-fix-v2";
if (typeof window !== "undefined") {
  window.__BUILD_MARKER__ = __BUILD_MARKER__;
}

// Pages
import HomePage from "./pages/HomePage";
import TeacherLogin from "./pages/TeacherLogin";
import TeacherRegister from "./pages/TeacherRegister";
import TeacherDashboard from "./pages/TeacherDashboard";
import CreateExam from "./pages/CreateExam";
import ExamResults from "./pages/ExamResults";
import EssayGrading from "./pages/EssayGrading";
import ExamAnalytics from "./pages/ExamAnalytics";
import LiveExamMonitor from "./pages/LiveExamMonitor";
import StudentLogin from "./pages/StudentLogin";
import StudentRegister from "./pages/StudentRegister";
import StudentPending from "./pages/StudentPending";
import StudentExamList from "./pages/StudentExamList";
import TakeExam from "./pages/TakeExam";
import StudentExamResults from "./pages/StudentExamResults";
import StudentAttempts from "./pages/StudentAttempts";
import TopicEditor from "./pages/TopicEditor";
import TakeTopicExam from "./pages/TakeTopicExam";
import TopicAttemptResults from "./pages/TopicAttemptResults";

// Protected Route Components
const TeacherRoute = ({ children }) => {
  const { isTeacher, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-emerald-700 rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return isTeacher ? children : <Navigate to="/teacher/login" replace />;
};

const StudentRoute = ({ children }) => {
  const { isStudent, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-emerald-700 rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return isStudent ? children : <Navigate to="/student/login" replace />;
};

const ApprovedStudentRoute = ({ children }) => {
  const { isStudent, isApprovedStudent, isPendingStudent, isRejectedStudent, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-emerald-700 rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!isStudent) {
    return <Navigate to="/student/login" replace />;
  }
  
  if (isPendingStudent || isRejectedStudent) {
    return <Navigate to="/student/pending" replace />;
  }
  
  return isApprovedStudent ? children : <Navigate to="/student/pending" replace />;
};

const SmartHomeRoute = () => {
  const { isTeacher, isApprovedStudent, isPendingStudent, isRejectedStudent, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-12 h-12 border-4 border-slate-200 border-t-emerald-700 rounded-full animate-spin"></div></div>;
  if (isApprovedStudent) return <Navigate to="/student/exams" replace />;
  if (isPendingStudent || isRejectedStudent) return <Navigate to="/student/pending" replace />;
  if (isTeacher) return <Navigate to="/teacher/dashboard" replace />;
  return <Navigate to="/student/login" replace />;
};

const SmartStudentLoginRoute = () => {
  const { isApprovedStudent, isPendingStudent, isRejectedStudent, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-12 h-12 border-4 border-slate-200 border-t-emerald-700 rounded-full animate-spin"></div></div>;
  if (isApprovedStudent) return <Navigate to="/student/exams" replace />;
  if (isPendingStudent || isRejectedStudent) return <Navigate to="/student/pending" replace />;
  return <StudentLogin />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<SmartHomeRoute />} />
      
      {/* Teacher Routes */}
      <Route path="/teacher/login" element={<TeacherLogin />} />
      <Route path="/teacher/register" element={<TeacherRegister />} />
      <Route 
        path="/teacher/dashboard" 
        element={
          <TeacherRoute>
            <TeacherDashboard />
          </TeacherRoute>
        } 
      />
      <Route 
        path="/teacher/exams/create" 
        element={
          <TeacherRoute>
            <CreateExam />
          </TeacherRoute>
        } 
      />
      <Route 
        path="/teacher/exams/:examId/results" 
        element={
          <TeacherRoute>
            <ExamResults />
          </TeacherRoute>
        } 
      />
      <Route 
        path="/teacher/exams/:examId/essay-grading" 
        element={
          <TeacherRoute>
            <EssayGrading />
          </TeacherRoute>
        } 
      />
      <Route 
        path="/teacher/exams/:examId/analytics" 
        element={
          <TeacherRoute>
            <ExamAnalytics />
          </TeacherRoute>
        } 
      />
      <Route 
        path="/teacher/exams/:examId/monitor" 
        element={
          <TeacherRoute>
            <LiveExamMonitor />
          </TeacherRoute>
        } 
      />
      <Route
        path="/teacher/exams/:examId/edit"
        element={
          <TeacherRoute>
            <CreateExam editMode={true} />
          </TeacherRoute>
        }
      />
      <Route
        path="/teacher/students/:studentId/attempts"
        element={
          <TeacherRoute>
            <StudentAttempts />
          </TeacherRoute>
        }
      />
      <Route
        path="/teacher/topics/:topicId"
        element={
          <TeacherRoute>
            <TopicEditor />
          </TeacherRoute>
        }
      />

      {/* Student Routes */}
      <Route path="/student/login" element={<SmartStudentLoginRoute />} />
      <Route path="/student/register" element={<StudentRegister />} />
      <Route 
        path="/student/pending" 
        element={
          <StudentRoute>
            <StudentPending />
          </StudentRoute>
        } 
      />
      <Route 
        path="/student/exams" 
        element={
          <ApprovedStudentRoute>
            <StudentExamList />
          </ApprovedStudentRoute>
        } 
      />
      <Route 
        path="/student/exam/:examId" 
        element={
          <ApprovedStudentRoute>
            <TakeExam />
          </ApprovedStudentRoute>
        } 
      />
      <Route
        path="/student/exam/:examId/results/:sessionId"
        element={
          <ApprovedStudentRoute>
            <StudentExamResults />
          </ApprovedStudentRoute>
        }
      />
      <Route
        path="/student/topic/:topicId/attempt"
        element={
          <ApprovedStudentRoute>
            <TakeTopicExam />
          </ApprovedStudentRoute>
        }
      />
      <Route
        path="/student/topic-attempts/:attemptId/results"
        element={
          <ApprovedStudentRoute>
            <TopicAttemptResults />
          </ApprovedStudentRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App" dir="rtl">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster 
          position="top-center" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              direction: 'rtl',
              fontFamily: 'Cairo, sans-serif',
            },
          }}
        />
      </AuthProvider>
    </div>
  );
}

export default App;
